import React, { useEffect, useState } from 'react';
import { Map, Marker, Popup, TileLayer } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { match, RouterProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import * as checkoutActions from '../../actions/checkout';
import { Facility, FacilityFeature } from '../../models/Facility';
import { IAppState } from '../../reducers';
import { Service as ServiceType } from '../../reducers/checkout';
import ErrorMessage from '../ErrorMessage';
import PhoneNumber from '../Format/PhoneNumber';
import PageTitle from '../PageTitle';

interface ISummaryUrlProps {
  state?: string
  name?: string
}

interface ISummaryProps extends RouterProps {
  match: match<ISummaryUrlProps>
}

const Summary: React.FC<ISummaryProps> = props => {
  const dispatch = useDispatch();
  const state = useSelector((appState: IAppState) => appState);
  const [facility, setFacility] = useState<Facility>(null);

  useEffect(() => {
    if (state.common.facilities) {
      const matches = state.common.facilities.filter(f =>
        f.state.toLowerCase() == props.match.params.state.toLowerCase() && f.getConfigurationValue('UrlName') == props.match.params.name.toLowerCase()
      );
      setFacility(matches[0]);
    }
  }, [state.common.facilities]);

  const hasWallet = facility && (
    facility.featureTexting ||
    facility.features.indexOf(FacilityFeature.KioskAudioCalls) > -1 ||
    facility.features.indexOf(FacilityFeature.WirelessAudioCalls) > -1 ||
    facility.features.indexOf(FacilityFeature.WirelessVideoCalls) > -1
  );

  const facilityOfflineMessage = facility?.getConfigurationValue('FacilityOfflineMessage');

  const hasPrepaidCollectCalls = facility && (
    facility.features.indexOf(FacilityFeature.PrepaidCollectCalls) > -1
  );

  return (
    <div>
      <PageTitle title={facility ? facility.name : 'Loading...'} />

      <div className="hero bg-primary text-light py-5">
        <div className="container">
          <div className="row pt-5">
            <div className="col-lg-8 offset-lg-2 pt-5">
              {facility ?
                <>
                  <h1 className="d-none">{facility.name}</h1>
                  <span className="page-icon rt-about"></span>
                  <h2>{facility.name}</h2>
                </> :
                <>
                  <span className="page-icon rt-about"></span>
                  <h2>Loading...</h2>
                </>
              }
            </div>
          </div>
        </div>
      </div>

      {facilityOfflineMessage &&
        <main className="container">
          <div className="row py-5">
            <div className="col-md-12 d-flex justify-content-center">
              <ErrorMessage
                severity="warning"
                message={facilityOfflineMessage} />
            </div>
          </div>
        </main>
      }

      {facility && facilityOfflineMessage == null &&
        <main className="container">
          {hasWallet &&
            <div className="row py-5">
              <div className="col-md-6 image-service srvc-inmatewallet d-flex justify-content-center">
                <span className="align-self-center"></span>
              </div>

              <div className="col-md-6 py-5">
                <h3>Inmate Wallet - All Options</h3>
                <p>The funds paid into an <b>Inmate Wallet - All Options</b> account are tied directly to an inmate’s personal device for them to use for texting, voice and video calls, and games, music and entertainment (where available). At most facilities, you can call the inmate directly. This account allows the inmate to communicate with multiple phone numbers. These funds are non-transferable and non-refundable. Credit card and transaction fees apply.</p>
                
                <button type="button" className="btn btn-action btn-block mt-3" onClick={() => {
                  dispatch(checkoutActions.selectFacility(facility));
                  dispatch(checkoutActions.selectService(ServiceType.Wallet));
                  props.history.push('/checkout/wallet');
                }}>Add Funds to Inmate Wallet - All Options Account</button>
              </div>
            </div>
          }

          <div className="row py-5 flex-row-reverse">
            <div className="col-md-6 image-service srvc-phonecard d-flex justify-content-center">
              <span className="align-self-center"></span>
            </div>

            <div className="col-md-6 py-5">
              <h3>Inmate Wallet - Voice & Video Calls Only</h3>
              <p>Funds paid into an <b>Inmate Wallet - Voice & Video Calls Only</b> account are tied directly to an inmate’s personal device for them to use for voice and video calling <b>ONLY</b>. At most facilities, you can call the inmate directly. This account allows the inmate to call multiple phone numbers. No fees apply to the purchase, and it is refundable.</p>
              <p>
                 <button
                 onClick={() => {
                  dispatch(checkoutActions.selectService(ServiceType.AudioVideo));
                  props.history.push('/checkout/audio-video');
                  return false;
                 }}
                 className="btn btn-block bg-action text-white">
                 Fund Inmate Wallet - Voice & Video Calls Only
                </button>
               </p>
            </div>
          </div>

          {hasPrepaidCollectCalls &&
            <div className="row py-5">
              <div className="col-md-6 image-service srvc-collectcall d-flex justify-content-center">
                <span className="align-self-center"></span>
              </div>

              <div className="col-md-6 py-5">
                <h3>Prepaid Collect Calls</h3>
                <p>Prepaying for collect calls allows friends and family to receive calls from inmates by funding specific telephone numbers. These calls can be received on a cell phone or landline. Funds in this account can only be used for calls to or from the specified telephone number. At most facilities, you can call the inmate directly. No fees apply to the purchase, and it is refundable.</p>
                <button type="button" className="btn btn-action btn-block mt-3" onClick={() => {
                  dispatch(checkoutActions.selectFacility(facility));
                  dispatch(checkoutActions.selectService(ServiceType.Collect));
                  props.history.push('/checkout/collect');
                }}>
                  Add funds to collect account
                </button>
              </div>
            </div>
          }

          {facility.visitationSchedulingEnabled &&
            <div className="row py-5 flex-row-reverse">
              <div className="col-md-6 image-service srvc-videovisit d-flex justify-content-center">
                <span className="align-self-center"></span>
              </div>

              <div className="col-md-6 py-5">
                <h3>Video Visitation</h3>
                <p>Our innovative Video Visitation system has gone beyond the traditional connections between low quality cameras and monitors to modern devices. Inmates can connect from a Reliance Tablet Station in their housing unit or from their personal Reliance Tablet Phone to a public Reliance Tablet Station. The system is designed to meet all on-site visitation requirements. For off-site video communication, Reliance Video Calls allows friends and family to receive see and hear the inmate directly on their Android or iOS device using our Reliance Connect app available in the App Store or Play Store.</p>
                <button type="button" className="btn btn-action btn-block mt-3" onClick={() => {
                  dispatch(checkoutActions.selectFacility(facility));
                  dispatch(checkoutActions.selectService(ServiceType.Visitation));
                  props.history.push('/visitation/schedule');
                }}>
                  Schedule a video visitation
                </button>
              </div>
            </div>
          }

          <div className="row">
            <div className="col-md-6">
              <h6>Address</h6>
              <p>
                {facility.address}<br/>
                {facility.city}, {facility.state} {facility.zip}
              </p>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${facility.latitude},${facility.longitude}`} target="_blank">Directions</a>
            </div>
            {facility.voicemail &&
              <div className="col-md-6">
                <h6>Voicemail</h6>
                <p>
                  <PhoneNumber value={facility.voicemail}/> {facility.voicemailExtension && <span>ext. {facility.voicemailExtension}</span>}
                </p>
              </div>
            }
          </div>

          {facility.latitude && facility.longitude &&
            <div className="row py-5 flex-row-reverse">
              <div className="col-md-12">
                <Map center={[facility.latitude, facility.longitude]} zoom={13} style={{ height: 600 }}>
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
                  />
                  <Marker position={[facility.latitude, facility.longitude]}>
                    <Popup>
                      {facility.name}<br/>
                      {facility.address}<br/>
                      {facility.city}, {facility.state} {facility.zip}<br/>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${facility.latitude},${facility.longitude}`} target="_blank">Directions</a>
                    </Popup>
                  </Marker>
                </Map>
              </div>
            </div>
          }
        </main>
      }
    </div>
  );
}

export default withRouter(Summary);
